/**
 * The wire contract shared by the gateway and the PWA.
 *
 * Everything the phone renders is derived from an ordered, persisted stream of
 * `AgentEvent`s. Nothing is held only in memory: a run keeps producing events
 * while the phone is asleep, and the client catches up by asking for
 * everything after the last sequence number it saw.
 */

import type { EffortId } from './models.ts';

/**
 * `paused` is not a terminal state: the run hit the Claude Code 5-hour usage
 * limit, and the gateway is holding it until the window reopens. It keeps its
 * place at the head of its session's queue and goes back to `queued` on its
 * own — see `Runner.pauseForLimit`.
 */
export type RunStatus = 'queued' | 'running' | 'success' | 'error' | 'cancelled' | 'paused';

/**
 * Which CLI backend runs a turn. Picked per message, from the model chosen in
 * Settings — see `models.ts`. Every adapter flattens its own wire format into
 * the one `AgentEvent` stream below, so nothing downstream of the runner knows
 * which agent produced a transcript. `cursor` / `claude` / `grok` /
 * `opencode` / `openai` are the five CLIs: Cursor, Claude Code, the xAI
 * Grok CLI, OpenCode, and OpenAI Codex.
 */
export type AgentId = 'cursor' | 'claude' | 'grok' | 'opencode' | 'openai';

export const IMAGE_MIMES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const;
export type ImageMime = (typeof IMAGE_MIMES)[number];

/**
 * Commands in `tools/bin` a run can be handed. The names are the binaries.
 * `projects.json` `tools` is an allowlist of these; omit it for all of them.
 */
export const AGENT_TOOL_IDS = ['agent-task', 'agent-video', 'agent-shot'] as const;
export type AgentToolId = (typeof AGENT_TOOL_IDS)[number];

export function isAgentToolId(value: unknown): value is AgentToolId {
  return typeof value === 'string' && (AGENT_TOOL_IDS as readonly string[]).includes(value);
}

/** How many photos one message can carry. The CLIs cope with a handful; more is noise. */
export const MAX_CHAT_IMAGES = 5;

/** A photo attached to a user message. Bytes live on disk; the log only stores the id. */
export interface ChatImage {
  id: string;
  mime: ImageMime;
}

/**
 * A screenshot an agent took of the project it is working on, cropped by the
 * agent to whatever it wanted to show. It is stored exactly as a photo is —
 * bytes under DATA_DIR, only the id in the log — because that is what it is;
 * the size is carried here so the phone can hold the space before it loads.
 */
export interface ChatShot {
  id: string;
  mime: ImageMime;
  width?: number;
  height?: number;
}

/** Playwright records WebM. MP4 is accepted so a transcoded demo still plays. */
export const VIDEO_MIMES = ['video/webm', 'video/mp4'] as const;
export type VideoMime = (typeof VIDEO_MIMES)[number];

/**
 * A screen recording an agent made of the project it is working on, stored the
 * same way a photo is: bytes on disk under DATA_DIR, only the id in the log.
 */
export interface ChatVideo {
  id: string;
  mime: VideoMime;
  /** Recording length in ms, when the recorder measured it. */
  durationMs?: number;
  width?: number;
  height?: number;
}

export type AgentEvent =
  /** A user message was accepted and queued as a run. */
  | { kind: 'user.message'; runId: string; text: string; images?: ChatImage[] }
  /** The agent process for a run actually started. */
  | { kind: 'run.started'; runId: string }
  /** Terminal event for a run. Always emitted, even on crash. */
  | { kind: 'run.finished'; runId: string; status: RunStatus; durationMs?: number; error?: string }
  /**
   * The run spent the Claude Code 5-hour window and is waiting for it to
   * reopen. Not terminal: a `run.resumed` follows, then `run.started` again.
   * `resumeAt` is unix ms, or null when nobody would say when.
   */
  | { kind: 'run.paused'; runId: string; resumeAt: number | null; reason: string }
  /** The wait ended and the run went back into its session's queue. */
  | { kind: 'run.resumed'; runId: string; attempt: number }
  /** Assistant prose. In partial-streaming mode `delta` arrives many times. */
  | { kind: 'assistant.start'; messageId: string }
  | { kind: 'assistant.delta'; messageId: string; text: string }
  | { kind: 'assistant.end'; messageId: string; text: string }
  /** Tool activity, already flattened out of cursor's nested shape. */
  | { kind: 'tool.started'; callId: string; tool: string; summary: string }
  | { kind: 'tool.completed'; callId: string; tool: string; summary: string; ok: boolean; detail?: string }
  /**
   * A screen recording the agent produced with `agent-video`. Its own event
   * rather than an attachment on an assistant message, because the recording
   * finishes whenever the browser closes - which is rarely where the prose is.
   */
  | { kind: 'agent.video'; video: ChatVideo; caption?: string }
  /**
   * A screenshot the agent took with `agent-shot`. Its own event for the same
   * reason a recording is: it is made at whatever moment the UI looked right,
   * which is rarely where the prose that mentions it ends up.
   */
  | { kind: 'agent.shot'; shot: ChatShot; caption?: string }
  /** Housekeeping the UI shows in a muted style. */
  | { kind: 'agent.info'; text: string }
  | { kind: 'agent.error'; text: string };

export interface StoredEvent {
  sessionId: string;
  seq: number;
  ts: number;
  event: AgentEvent;
}

export interface Project {
  id: string;
  name: string;
  path: string;
  /** Overrides CURSOR_MODEL for this repo. */
  model?: string | null;
  /** Extra cursor-agent flags for this repo, e.g. ["--sandbox", "workspace"]. */
  args?: string[];
  /** Extra claude flags for this repo, e.g. ["--add-dir", "../shared"]. */
  claudeArgs?: string[];
  /** Extra grok flags for this repo, e.g. ["--sandbox", "workspace"]. */
  grokArgs?: string[];
  /** Extra opencode flags for this repo, e.g. ["--agent", "build"]. */
  opencodeArgs?: string[];
  /** Extra Codex flags for this repo, e.g. ["--skip-git-repo-check"]. */
  openaiArgs?: string[];
  /**
   * Shell command the deploy button runs in `path`. Lives in projects.json on
   * purpose — nothing reachable over HTTP can change it.
   */
  deploy?: string | null;
  /**
   * Optional LAN desktop. When that host answers SSH *and* the project has
   * not been pinned to the home server, agents, Git, and Files use
   * `desktop.path` over a full shell. When it does not — or the phone pinned
   * `server` — they use `path` on this machine. After a server-side Push, the
   * gateway still `git pull`s the follower if this field is set.
   */
  desktop?: DesktopTarget | null;
  /**
   * Commands from `tools/bin` a run in this repo may use. The phone's Config
   * → Projects tab writes this field; it cannot add a project or change
   * `path` / `deploy` / `desktop`. Omit for every tool that is switched on
   * globally; `[]` for none.
   */
  tools?: AgentToolId[];
}

/** Windows (or other) clone used when that host answers SSH. */
export interface DesktopTarget {
  host: string;
  path: string;
}

/**
 * Where agents, Git, and Files should run when a desktop is configured.
 * `desktop` (default) uses the PC while it answers SSH; `server` stays on
 * this machine even then. A down PC always falls back to the server.
 */
export type HostPref = 'desktop' | 'server';

export type DeployStatus = 'running' | 'success' | 'error' | 'cancelled';

/** Latest deploy of a project. The phone renders this; the command is not in the request. */
export interface DeploySnapshot {
  projectId: string;
  command: string;
  status: DeployStatus;
  output: string;
  startedAt: number;
  endedAt: number | null;
  error: string | null;
  /** True when `output` was capped and the start of the log was dropped. */
  truncated: boolean;
}

/** Latest desktop command (e.g. Run Dev). Same shape as deploy so the phone can stream a log. */
export type CommandStatus = DeployStatus;

export interface CommandSnapshot {
  projectId: string;
  commandId: string;
  label: string;
  port: number | null;
  status: CommandStatus;
  output: string;
  startedAt: number;
  endedAt: number | null;
  error: string | null;
  truncated: boolean;
}

/**
 * One model a provider's CLI says you may pick. `arg` is the provider's own
 * spelling, which is what a run passes to `--model`; `id` is what the phone
 * and the session table store. Shipped rows keep their curated id, label, and
 * effort rungs; discovered rows use the provider's id for all three.
 */
export interface ProviderModel {
  id: string;
  agent: AgentId;
  label: string;
  arg: string;
  efforts: EffortId[];
  /** True when this row comes from the catalog this build ships with. */
  builtin: boolean;
}

/** Every model one backend offers, plus why the list may be short. */
export interface ProviderCatalog {
  agent: AgentId;
  label: string;
  /** Where the list came from: the CLI itself, or the shipped catalog. */
  source: 'cli' | 'builtin';
  /** The command that was asked, for the hint under a failed list. */
  command: string | null;
  models: ProviderModel[];
  error: string | null;
}

/** More → Models: what every provider offers, and what the phone may pick. */
export interface ModelCatalog {
  providers: ProviderCatalog[];
  /** Ids allowed in chats and tasks. Empty means "no filter — offer all". */
  enabled: string[];
  /** When the CLIs were last asked, in unix ms. */
  fetchedAt: number;
}

/** One run of a provider's install line. Same shape as a deploy's log. */
export type CliUpdateStatus = DeployStatus;

export interface CliUpdateSnapshot {
  agent: AgentId;
  command: string;
  status: CliUpdateStatus;
  output: string;
  startedAt: number;
  endedAt: number | null;
  error: string | null;
  /** True when `output` was capped and the start of the log was dropped. */
  truncated: boolean;
}

/** More → Updates: one backend's CLI as this gateway currently has it. */
export interface CliUpdateRow {
  agent: AgentId;
  label: string;
  /** The configured binary name or path, before lookup. */
  bin: string;
  /** Where it was found, or null when this CLI is not installed here. */
  path: string | null;
  /** What `<bin> --version` said, or null when it could not be asked. */
  version: string | null;
  /** The command Update runs. Fixed per backend, not configurable. */
  command: string;
  /** The last update run since this gateway started, or null. */
  snapshot: CliUpdateSnapshot | null;
}

export interface CliUpdateList {
  clis: CliUpdateRow[];
  /** When the versions were last read, in unix ms. */
  fetchedAt: number;
}

export interface DeployState {
  configured: boolean;
  command: string | null;
  snapshot: DeploySnapshot | null;
}

export interface Session {
  id: string;
  projectId: string;
  title: string;
  /**
   * Conversation ids keyed by host and backend (`cursor` / `claude` / `grok`
   * / `opencode` on the server, `h:<host>:<agent>` on a desktop). Ids are not
   * interchangeable across machines or CLIs.
   */
  agentChatIds: Record<string, string>;
  /** Last machine a run on this chat used: `server` or a desktop hostname. */
  lastHost: string | null;
  createdAt: number;
  updatedAt: number;
  /**
   * Home-screen rank within a project. Lower is higher on the list. Running a
   * chat must not change this — only an explicit reorder (or a newly created
   * chat, which lands at the top) does.
   */
  sortOrder: number;
  /**
   * Parked off the home list. Backlog chats keep their transcript and task;
   * they just do not occupy Home until restored.
   */
  backlogged: boolean;
  lastSeq: number;
  /** Status of the most recent run, so the list can show a spinner. */
  runStatus: RunStatus | null;
  preview: string;
  /** Saved task prompt. Empty means this chat has no task. */
  task: string;
  /** Photos attached to the saved task. Empty when the task is text-only. */
  taskImages: ChatImage[];
  /**
   * Repo-relative paths the task is expected to touch, assessed when it was
   * filed — see `surface.ts`. Empty means nobody assessed it. Two tasks whose
   * surfaces overlap cannot sit in the same project queue together.
   */
  taskSurface: string[];
  /**
   * When true, a task run appends `SKIP_VERIFICATION_NOTE` to the prompt the
   * agent sees. The saved task text is left alone.
   */
  skipVerification: boolean;
  /** Allow this task to run alongside other tasks in the same project. */
  parallelTask: boolean;
  /**
   * When true, every event of this chat is also appended, in readable form, to
   * a file under the repo's `logs/` folder — see `session-log.ts`. Off by
   * default; it is a review aid, not part of how a run works.
   */
  logging: boolean;
  /** True after the current `task` text has been run successfully. */
  taskDone: boolean;
  /** Unix ms of the latest user submit. Null until the first run. */
  lastSubmittedAt: number | null;
  /** Unix ms when the latest run finished. Null while the first run is in flight. */
  lastEndedAt: number | null;
  /** Model picked in Settings for the next turn. Null until the user opens the chat. */
  model: string | null;
  /** Effort rung paired with `model`. Null when the family has no rungs. */
  effort: string | null;
  /** Cumulative input tokens across finished turns. Zero until a run reports usage. */
  inputTokens: number;
  /** Cumulative output tokens across finished turns. Zero until a run reports usage. */
  outputTokens: number;
}

/** True when a session has a saved task (text and/or photos). */
export function sessionHasTask(session: Pick<Session, 'task' | 'taskImages'>): boolean {
  return session.task.trim().length > 0 || session.taskImages.length > 0;
}

/**
 * Extra instruction a task-tab toggle can pin onto the prompt. It is not part
 * of the saved task, so editing the prompt never has to strip it back out.
 */
export const SKIP_VERIFICATION_NOTE =
  "Don't do any testing or verification. Just implement your best guess. I will take care of verification myself.";

/** Append the skip-verification note when the session toggle is on. */
export function withSkipVerification(prompt: string, enabled: boolean): string {
  if (!enabled) return prompt;
  const body = prompt.trimEnd();
  return body ? `${body}\n\n${SKIP_VERIFICATION_NOTE}` : SKIP_VERIFICATION_NOTE;
}

export type SessionOrder = Pick<Session, 'id' | 'createdAt' | 'sortOrder'>;

/** Home-screen order: `sortOrder` ascending, then newest created first. */
export function compareSessions(a: SessionOrder, b: SessionOrder): number {
  return a.sortOrder - b.sortOrder || b.createdAt - a.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

export interface Run {
  id: string;
  sessionId: string;
  prompt: string;
  /** Photos attached to this turn. Empty when the message was text-only. */
  images: ChatImage[];
  /** Which CLI this turn is for, resolved from the model at submit time. */
  agent: AgentId;
  /** The `--model` argument for this turn; null falls back to project/env.
   *  For cursor, effort is already folded in (e.g. `cursor-grok-4.6-medium`);
   *  for claude, grok, opencode, and openai it is carried separately in `effort`. */
  model: string | null;
  /** The effort rung the user picked, kept so a queued run survives a restart. */
  effort: string | null;
  status: RunStatus;
  startedAt: number | null;
  endedAt: number | null;
  /** While `paused`, the unix ms the usage window is expected to reopen. */
  resumeAt: number | null;
  error: string | null;
  /** `task` runs auto-commit when they succeed. */
  source: 'chat' | 'task';
}

/** A decoration on a commit (`HEAD -> main`, `origin/main`, `tag: v1`). */
export type GitRefKind = 'head' | 'branch' | 'remote' | 'tag';

export interface GitRef {
  name: string;
  kind: GitRefKind;
}

export interface GitCommit {
  hash: string;
  shortHash: string;
  subject: string;
  author: string;
  /** Unix milliseconds. */
  timestamp: number;
  refs: GitRef[];
  head: boolean;
}

/** One line of `git log --graph`. `commit` is null on connector-only rows. */
export interface GitLogRow {
  graph: string;
  commit: GitCommit | null;
}

export interface GitChange {
  /** Two-letter porcelain XY status (`M `, `??`, `R `). */
  xy: string;
  path: string;
  origPath?: string;
  /** Lines added in the working tree diff, when known. */
  additions?: number;
  /** Lines removed in the working tree diff, when known. */
  deletions?: number;
}

export interface GitSnapshot {
  branch: string;
  detached: boolean;
  upstream: string | null;
  ahead: number;
  behind: number;
  dirty: boolean;
  changes: GitChange[];
  /** True when `changes` was capped. */
  changesTruncated: boolean;
  log: GitLogRow[];
  /** Active `gh` login used for git push, or null if gh is missing / signed out. */
  githubUser: string | null;
  /**
   * After a push: the origin push succeeded but the optional desktop `git pull`
   * did not. Absent when there is no follower or the pull succeeded.
   */
  desktopError?: string | null;
  /**
   * Opening Git tried to fast-forward onto origin and could not (dirty overlap,
   * lock, …). Absent when there was nothing to pull or the merge succeeded.
   */
  syncError?: string | null;
  /** `server` or a desktop hostname — which clone this snapshot was taken from. */
  activeHost?: string;
}

/** Total working-tree line changes vs HEAD (staged + unstaged + untracked). */
export interface GitDiffstat {
  additions: number;
  deletions: number;
}

/** Unified patch for one dirty path (`git diff` / `--cached` / untracked). */
export interface GitDiff {
  path: string;
  origPath?: string;
  patch: string;
  truncated: boolean;
}

/** Unified patch for one commit (`git show` vs first parent). */
export interface GitCommitDiff {
  hash: string;
  patch: string;
  truncated: boolean;
}

/** What a row in the file browser points at. Symlinks are resolved to their target kind. */
export type FileKind = 'dir' | 'file' | 'other';

export interface FileEntry {
  name: string;
  /** Slash-separated path from the project root. Never empty. */
  path: string;
  kind: FileKind;
  /** Null when the entry could not be stat'ed (broken symlink, race). */
  size: number | null;
  /** Unix ms of the last modification, or null. */
  mtime: number | null;
}

/** One directory of a project working tree, as the phone browses it. */
export interface DirListing {
  /** Slash-separated path from the project root. Empty string is the root. */
  path: string;
  /** Path of the enclosing directory, or null at the root. */
  parent: string | null;
  entries: FileEntry[];
  /** True when `entries` was capped. */
  truncated: boolean;
}

/** The text of one project file, bounded so a huge file cannot swamp the phone. */
export interface FileContent {
  path: string;
  /** Size on disk, even when `text` was cut short. */
  size: number;
  mtime: number | null;
  text: string;
  /** True when only the head of the file is in `text`. */
  truncated: boolean;
  /** True when the bytes are not text; `text` is then empty. */
  binary: boolean;
}

export interface GitBranch {
  name: string;
  current: boolean;
}

export interface GitBranchesSnapshot {
  branches: GitBranch[];
  currentBranch: string;
}

/**
 * Cursor plan usage for the current billing cycle.
 * `ok: false` when the CLI is not logged in or Cursor's dashboard API is unreachable.
 */
export type CursorUsage =
  | { ok: false }
  | {
      ok: true;
      /** Unix ms when the current billing cycle started. */
      startedAt: number | null;
      /** Unix ms when the current billing cycle ends. */
      resetsAt: number | null;
      /** Composer / Auto / Cursor-native models, 0–100+. */
      autoPercent: number | null;
      /** Named API models (Opus, Grok, …), 0–100+. */
      apiPercent: number | null;
    };

/**
 * Claude Code subscription usage (Pro / Max OAuth plans).
 * `ok: false` when not logged in, on API-key billing, or the usage API is unreachable.
 */
export type ClaudeUsage =
  | { ok: false }
  | {
      ok: true;
      /** Rolling 5-hour window, 0–100+. */
      sessionPercent: number | null;
      /** Rolling 7-day window, 0–100+. */
      weekPercent: number | null;
      /** Unix ms when the 5-hour window resets. */
      sessionResetsAt: number | null;
      /** Unix ms when the 7-day window resets. */
      weekResetsAt: number | null;
    };

/**
 * SuperGrok / Grok Build plan usage (the weekly window `/usage` shows).
 * `ok: false` when `grok login` has not run or the CLI billing proxy is unreachable.
 */
export type GrokUsage =
  | { ok: false }
  | {
      ok: true;
      /** Current period usage, 0–100+. Weekly on SuperGrok. */
      percent: number | null;
      /** Unix ms when the current period started. */
      startedAt: number | null;
      /** Unix ms when the current period resets. */
      resetsAt: number | null;
    };

/**
 * OpenAI Codex subscription windows, plus optional API spend for phone
 * dictation (gpt-live-transcribe) this calendar month. `ok: false` when
 * neither `codex login` nor the Costs API yields a usable snapshot.
 */
export type OpenAIUsage =
  | { ok: false }
  | {
      ok: true;
      /** Primary Codex allowance, 0–100. Null for API-key-only accounts. */
      primaryPercent: number | null;
      /** Unix ms when the primary allowance resets. */
      primaryResetsAt: number | null;
      /** Length of the primary allowance window, in seconds. */
      primaryWindowSeconds: number | null;
      /** Secondary Codex allowance, when the plan has one. */
      secondaryPercent: number | null;
      /** Unix ms when the secondary allowance resets. */
      secondaryResetsAt: number | null;
      /** Length of the secondary allowance window, in seconds. */
      secondaryWindowSeconds: number | null;
      /** USD spent on transcription line items this UTC month. */
      usd: number | null;
      /** Unix ms at the start of the billed month (UTC). */
      monthStart: number | null;
    };

/**
 * OpenCode Go plan usage — the rolling 5-hour, weekly, and monthly windows the
 * console shows. `ok: false` when `opencode auth login --provider opencode-go`
 * has not run or the usage API is unreachable.
 */
export type OpenCodeUsage =
  | { ok: false }
  | {
      ok: true;
      /** Rolling 5-hour window, 0–100. */
      rollingPercent: number | null;
      /** Unix ms when the rolling window resets. */
      rollingResetsAt: number | null;
      /** Weekly window, 0–100. */
      weekPercent: number | null;
      /** Unix ms when the weekly window resets. */
      weekResetsAt: number | null;
      /** Monthly window, 0–100. */
      monthPercent: number | null;
      /** Unix ms when the monthly window resets. */
      monthResetsAt: number | null;
    };

/** Home-screen usage card: Cursor, Claude Code, Grok, OpenCode, and dictation. */
export type UsageResponse = {
  cursor: CursorUsage;
  claude: ClaudeUsage;
  grok: GrokUsage;
  openai: OpenAIUsage;
  opencode: OpenCodeUsage;
};

/** client -> server */
export type ClientMessage =
  | { t: 'subscribe'; sessionId: string; sinceSeq: number }
  | { t: 'unsubscribe'; sessionId: string }
  | { t: 'ping' };

/** server -> client */
export type ServerMessage =
  | { t: 'event'; sessionId: string; seq: number; ts: number; event: AgentEvent }
  | { t: 'synced'; sessionId: string; seq: number }
  | { t: 'session'; session: Session }
  | { t: 'deploy'; snapshot: DeploySnapshot }
  | { t: 'command'; snapshot: CommandSnapshot }
  | { t: 'desktop'; online: string[] }
  | { t: 'pong' }
  | { t: 'error'; message: string };
